import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { useAuth } from "../context/AuthContext";
import excelIcon from "/icons/excel-icon.webp";
import * as XLSX from "xlsx";

//  import showStructuredErrors from "../utils/showErrors"

const ImportExcelPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  // Download sample Excel format
  const downloadSampleFormat = () => {
    // Sample data with exact headers from your CSV
    const sampleData = [
      {
        "Customer": "Kavita Mehta",
        "Phone": "9845981807",
        "Email": "kavita.mehta@example.com",
        "Company": "ICICI Lombard",
        "Category": "General Insurance",
        "Insurance Type": "Marine Insurance",
        "Policy ID": "POL164693",
        "Sum Assured": "100000",
        "Premium": "5000",
        "Due Date": "2024-02-07"
      },
      {
        "Customer": "Arjun Yadav",
        "Phone": "9854231940",
        "Email": "arjun.yadav@example.com",
        "Company": "Tata AIG",
        "Category": "General Insurance",
        "Insurance Type": "Health Insurance",
        "Policy ID": "POL232707",
        "Sum Assured": "1000000",
        "Premium": "8000",
        "Due Date": "2024-11-18"
      },
      {
        "Customer": "Sneha Patel",
        "Phone": "9891709141",
        "Email": "sneha.patel@example.com",
        "Company": "Tata AIG",
        "Category": "General Insurance",
        "Insurance Type": "Fire Insurance",
        "Policy ID": "POL380920",
        "Sum Assured": "500000",
        "Premium": "12000",
        "Due Date": "2024-07-10"
      }
    ];

    // Create workbook and worksheet
    const worksheet = XLSX.utils.json_to_sheet(sampleData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sample Data");

    // Set column widths for better readability
    const colWidths = [
      { wch: 20 }, // Customer
      { wch: 15 }, // Phone
      { wch: 30 }, // Email
      { wch: 25 }, // Company
      { wch: 20 }, // Category
      { wch: 25 }, // Insurance Type
      { wch: 15 }, // Policy ID
      { wch: 15 }, // Sum Assured
      { wch: 12 }, // Premium
      { wch: 15 }  // Due Date
    ];
    worksheet['!cols'] = colWidths;

    // Generate and download file
    XLSX.writeFile(workbook, "insurance_data_sample_format.xlsx");
  };

  // --- unchanged API/upload handler ---
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const agent = JSON.parse(localStorage.getItem("user"));
    const formData = new FormData();
    formData.append("file", file);
    formData.append("agentId", agent.id);
    formData.append("agentCode", localStorage.getItem("agentCode") || "AG01");

    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/agent/upload-excel`, {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
//  if (!res.ok) {
//   showStructuredErrors(data);
// }
      alert("Excel uploaded and saved to DB!");
      navigate("/agent/dashboard");
    } catch (err) {
      console.error("Upload error:", err);
      alert("Upload failed");
    }
  };

  // Right panel items (shown as mini cards)
  const reqItems = [
    { icon: "bi-person-badge", title: "Customer Name", desc: "Full name (required)" },
    { icon: "bi-telephone", title: "Phone No", desc: "With country code" },
    { icon: "bi-envelope", title: "Email", desc: "Valid email address" },
    { icon: "bi-geo-alt", title: "Address", desc: "Residential / business" },
    { icon: "bi-building", title: "Company Name", desc: "Insurer / organization" },
    { icon: "bi-card-checklist", title: "Insurance Type", desc: "Health, Auto, Life…" },
    { icon: "bi-diagram-3", title: "Insurance Category", desc: "Category / subcategory" },
    { icon: "bi-calendar-day", title: "Start Date ", desc: "Policy start date (DD‑MM‑YYYY)" },
    { icon: "bi-calendar2-week", title: "Due Date", desc: "Premium due date (DD‑MM‑YYYY)" },
    { icon: "bi-currency-dollar", title: "Premium Amount", desc: "Numeric value" },
    { icon: "bi-hash", title: "Policy ID", desc: "Unique identifier" },
    { icon: "bi-shield-plus", title: "Sum Assured", desc: "Total coverage value" },
  ];

  return (
    <div className="dashboard-layout d-flex">
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      {sidebarOpen && <div className="overlay" onClick={toggleSidebar}></div>}

      <div className="main-content flex-grow-1">
        <div className="import-excel-page container py-4">

          {/* Header */}
          <div className="d-flex justify-content-between align-items-start mb-4 flex-wrap gap-3">
            <div className="d-flex align-items-center gap-3">
              <div
                className="rounded-3 bg-success-subtle text-success d-inline-flex align-items-center justify-content-center"
                style={{ width: 44, height: 44 }}
              >
                <i className="bi bi-file-earmark-spreadsheet fs-5"></i>
              </div>
              <div>
                <h3 className="fw-bold mb-0">Import Excel Data</h3>
                <small className="text-muted">
                  Upload your customer and insurance data in Excel format
                </small>
              </div>
            </div>
          </div>

          {/* Two-column: left upload / right requirements */}
          <div className="row g-3">
            {/* LEFT */}
            <div className="col-lg-7 col-xl-6">
              <div className="upload-box p-4 p-md-4 rounded bg-white shadow-sm h-100">
                <div className="text-center mb-3">
                  <img src={excelIcon} alt="Excel Icon" className="upload-icon mb-3" />
                  <h5 className="fw-bold mb-1">Upload Excel File</h5>
                  <p className="text-muted mb-3">
                    Select an Excel file (.xlsx) containing insurance data
                  </p>
                </div>

                {/* Dashed drop area (clickable) */}
                <div
                  className="upload-area w-100 d-block p-4 p-md-5 mb-3"
                  role="button"
                  aria-label="Choose Excel file"
                  onClick={() => document.getElementById("fileUpload").click()}
                >
                  <div className="text-center">
                    <i className="bi bi-cloud-arrow-up fs-1"></i>
                    <p className="mb-1 mt-2 fw-semibold">Click to upload Excel file</p>
                    <small className="text-muted">Supports .xlsx files up to 10MB</small>
                  </div>
                </div>

                {/* Hidden input (keeps your existing upload flow) */}
                <input
                  id="fileUpload"
                  type="file"
                  accept=".xlsx"
                  onChange={handleFileChange}
                  hidden
                />

                {/* Buttons sit BELOW the dashed container */}
                <div className="d-flex justify-content-center gap-3 mb-4 flex-wrap">
                  <button
                    type="button"
                    className="btn btn-manual px-4"
                    onClick={() => document.getElementById("fileUpload").click()}
                  >
                    Upload File
                  </button>
                  <button
                    type="button"
                    className="btn btn-manual px-4"
                    onClick={downloadSampleFormat}
                  >
                    <i className="bi bi-download me-2"></i>
                    Download Sample
                  </button>
                </div>

                {/* Help / Steps */}
                <div className="card border-0 shadow-sm rounded-4">
                  <div className="card-body">
                    <h6 className="fw-bold mb-2">How it works</h6>
                    <ol className="text-muted small mb-0 ps-3">
                      <li>Download/export your data to a .xlsx file.</li>
                      <li>Ensure the first row contains the exact column headers below.</li>
                      <li>Click the box above or the button to choose your file and upload.</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT */}
            <div className="col-lg-5 col-xl-6">
              <aside className="req-panel card border-0 shadow-sm rounded-4 h-100">
                <div className="card-body p-4 p-md-4">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <i className="bi bi-info-circle text-primary"></i>
                    <h6 className="fw-bold mb-0">Excel File Format Requirements</h6>
                  </div>
                  <p className="text-muted small">
                    Ensure your Excel file includes the following columns with proper data formatting:
                  </p>

                  <div className="req-grid">
                    {reqItems.map((it, idx) => (
                      <div key={idx} className="req-item">
                        <div className="req-icon">
                          <i className={`bi ${it.icon}`} />
                        </div>
                        <div className="req-text">
                          <div className="req-title">{it.title}</div>
                          <div className="req-desc">{it.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="req-tip">
                    <i className="bi bi-lightbulb"></i>
                    <span>
                      Tip: Make sure your Excel file has headers in the first row that match these
                      field names for automatic mapping.
                    </span>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportExcelPage;